import React, { useEffect, useState } from "react";

import ChipPopoverTrigger from "@/components/ChipPopoverTrigger";
import { Popover, PopoverContent } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";

interface ChipRangeProps {
  label: React.ReactNode;
  min: number;
  minLabel?: React.ReactNode;
  max: number;
  maxLabel?: React.ReactNode;
  onChange: (newFilter: { lower: number; upper: number } | undefined) => unknown;
  step: number;
  value: number[];
  disabled?: boolean;
}

function ChipRange(props: ChipRangeProps) {
  const [open, setOpen] = useState(false);
  const { label, min, minLabel, max, maxLabel, onChange, step, value, disabled } = props;
  const [localValue, setLocalValue] = useState(value);

  const active = value && !(value[0] === min && value[1] === max);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const normalizeRange = (next: number | readonly number[]) => {
    const range = Array.isArray(next) ? next : [next];
    return range.length >= 2 ? [range[0], range[1]] : localValue;
  };

  const handleChange = (next: number | readonly number[]) => {
    setLocalValue(normalizeRange(next));
  };

  const handleCommit = (next: number | readonly number[]) => {
    const range = normalizeRange(next);
    onChange({ lower: range[0], upper: range[1] });
  };

  const handleClear = () => {
    setLocalValue([min, max]);
    onChange({ lower: min, upper: max });
  };

  return (
    // Ensure that escaping the popover without committing the value 
    // resets the value to the state's value
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        setLocalValue(value);
      }}
    >
      <ChipPopoverTrigger
        active={active}
        clearable
        disabled={disabled}
        onClear={handleClear}
      >
        {label}
      </ChipPopoverTrigger>
      <PopoverContent align="center" side="bottom" className="w-auto p-4">
        {/* Row: min label, range slider, max label */}
        <div className="flex w-75 items-center gap-2">
          <div className="shrink-0">{minLabel ? minLabel : min}</div>
          <Slider
            className="flex-1"
            value={localValue}
            onValueChange={handleChange}
            onValueCommitted={handleCommit}
            step={step}
            min={min}
            max={max}
            disabled={disabled}
            showValueTooltip
            formatValue={(current) => current.toLocaleString()}
            aria-label="Range"
          />
          <div className="shrink-0">{maxLabel ? maxLabel : max}</div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default ChipRange;
